import { Device } from "@/features/devices/types";
import {
  TrustLifecycleStatus,
  trustLifecycleLabel,
  trustLifecycleStatus,
} from "@/features/devices/firstTrust";

/** Row shape consumed by the existing Device Assignments table. */
export type DeviceAssignmentRow = {
  id: string;
  productName: string;
  serialNumber: string;
  customId: string;
  status: string;
  assignedTo: string;
  masterAccountId: string;
  contactEmail: string;
  contactPhone: string;
  location: string;
  locationId: string;
  zone: string;
  zoneId: string;
  lastSeen: string;
  inventoryStatus: string;
  enrollmentStatus: string;
  credentialBound: boolean;
  activationStatus: string;
  firstTrustStatus: string;
  firstTrustExpiresAt: string;
  firstTrustWindowId: string;
  firstTrustPurpose: string;
  pendingTrustKind: string;
  pendingTrustDeviceId: string;
  trustLifecycle: TrustLifecycleStatus | null;
};

/**
 * Map canonical V2 Device Fleet list rows into the Device Assignments table.
 * Serial Number remains the primary human-facing identity.
 */
export function mapFleetDeviceToAssignmentRow(device: Device): DeviceAssignmentRow {
  const serial = (device.serial_number || "").trim();
  const model = (device.model || "").trim();
  const rowBase = {
    id: device.id,
    productName: model || "KK-Player",
    serialNumber: serial,
    customId: device.custom_id || "",
    status: device.inventory_status || device.status || "",
    assignedTo: device.master_account || "",
    masterAccountId: device.master_account_id || "",
    contactEmail: "",
    contactPhone: "",
    location: device.location_name || "",
    locationId: device.location_id || "",
    zone: device.zone_name || "",
    zoneId: device.zone_id || "",
    lastSeen: device.last_seen_at || "",
    inventoryStatus: device.inventory_status || "",
    enrollmentStatus: device.enrollment_status || "",
    credentialBound: device.credential_bound === true,
    activationStatus: device.activation_status || "",
    firstTrustStatus: device.first_trust_status || "",
    firstTrustExpiresAt: device.first_trust_expires_at || "",
    firstTrustWindowId: device.first_trust_window_id || "",
    firstTrustPurpose: device.first_trust_purpose || "",
    pendingTrustKind: device.pending_trust_kind || "",
    pendingTrustDeviceId: device.pending_trust_device_id || "",
  };
  return {
    ...rowBase,
    trustLifecycle: trustLifecycleStatus(rowBase),
  };
}

export function statusBadgeClass(status: string): string {
  const key = (status || "").toLowerCase().replace(/\s+/g, "-");
  if (key === "online" || key === "good" || key === "enrolled") return "online";
  if (key === "offline") return "offline";
  if (
    key === "in-warehouse" ||
    key === "unclaimed" ||
    key === "maintenance" ||
    key === "enrollment-required" ||
    key === "re-enrollment-required"
  ) {
    return "maintenance";
  }
  return "offline";
}

/** Compact Clients-style Bootstrap icon for inventory / trust status. */
export function statusIcon(status: string): { icon: string; title: string } {
  const key = (status || "").toLowerCase().replace(/\s+/g, "-");
  if (key === "enrollment-required") {
    return { icon: "bi bi-shield-plus", title: "Enrollment Required" };
  }
  if (key === "re-enrollment-required") {
    return { icon: "bi bi-arrow-repeat", title: "Re-enrollment Required" };
  }
  if (key === "enrolled") {
    return { icon: "bi bi-shield-check", title: "Enrolled" };
  }
  if (key === "in-warehouse") {
    return { icon: "bi bi-box-seam", title: "In-warehouse" };
  }
  if (key === "installed" || key === "good" || key === "online") {
    return { icon: "bi bi-check-circle", title: "Installed" };
  }
  if (key === "unclaimed") {
    return { icon: "bi bi-question-circle", title: "Unclaimed" };
  }
  if (key === "offline") {
    return { icon: "bi bi-x-circle", title: "Offline" };
  }
  return { icon: "bi bi-circle", title: status || "Unknown status" };
}

export function displayTrustOrInventoryStatus(row: DeviceAssignmentRow): string {
  const lifecycle = row.trustLifecycle || trustLifecycleStatus(row);
  const label = trustLifecycleLabel(lifecycle);
  if (label) return label;
  return (row.inventoryStatus || row.status || "—").toUpperCase();
}
